import { Injectable } from "@angular/core";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { Action, select, Store } from "@ngrx/store";
import { AlertButtonColor, AlertButtonStyle } from "@obg/mat.ui.core";
import { ActionWithPayload, batchActions } from "@obg/ngrx-utils";
import {
	AbstractTranslatableAlertService,
	AbstractTranslatableSnackBarService,
	CORE_ROUTE,
	LimitsReminderAction,
	ProductType,
	SessionAction,
	SessionSelector,
} from "@obg/ngx.core";
import { CoreContentState, ImageSelector, TranslationGroup } from "@obg/ngx.core.content";
import { DeviceConfigService } from "@obg/ngx.global";
import { RouteAction } from "@obg/ngx.router";
import {
	KsaLimitAlertType,
	KsaLimitsAlertContainer,
	LotbaSelfExclusionAlertContainer,
	ZhhBettingLimitsAlertContainer,
} from "@obg/ngx.ui.common";
import { ignoreNil } from "@obg/rxjs";
import { filter, map, switchMap, take } from "rxjs/operators";

import { CustomerInteractionsAction } from "../customer-interactions/customer-interactions.action";
import { CustomerLimitSnackBarPayload } from "../customer-limit-notifications/customer-limit-notifications.model";
import {
	BalanceLimitExceededRealTimeMessagePayload,
	BettingLimitReachedRealTimeMessagePayload,
	DepositLimitReachedRealTimeMessagePayload,
} from "../real-time-notifications/real-time-notifications.model";
import { NotificationResultAction } from "../rg-interactions/notification-result/notification-result.action";
import { NotificationResultRedirectAction } from "../rg-interactions/notification-result/notification-result.model";
import { RgInteractionType } from "../rg-interactions/rg-interactions.model";

import { notificationMessageKeys } from "./notification-message-keys.const";

const MAT_NOTIFICATIONS_UKGC_RG_INTERACTIONS_ALERT_ID = "MAT_NOTIFICATIONS_UKGC_RG_INTERACTIONS_ALERT_ID";
const SESSION_LIMIT_REACHED_ALERT_ID = "SESSION_LIMIT_REACHED_ALERT_ID";
const DEPOSIT_LIMIT_REACHED_ALERT_ID = "DEPOSIT_LIMIT_REACHED_ALERT_ID";
const BETTING_LIMIT_REACHED_ALERT_ID = "BETTING_LIMIT_REACHED_ALERT_ID";
const BALANCE_LIMIT_REACHED_ALERT_ID = "BALANCE_LIMIT_REACHED_ALERT_ID";
const SELF_EXCLUSION_ALERT_ID = "SELF_EXCLUSION_ALERT_ID";
const LIMIT_ALERT_ID = "LIMIT_ALERT_ID";
const CUSTOMER_INTERACTION_TIME_TRIGGER_ID = "CUSTOMER_INTERACTION_TIME_TRIGGER_ID";
const CUSTOMER_INTERACTION_TOOLS_TRIGGER_ID = "CUSTOMER_INTERACTION_TOOLS_TRIGGER_ID";
const CUSTOMER_INTERACTION_WAGERING_TRIGGER_ID = "CUSTOMER_INTERACTION_WAGERING_TRIGGER_ID";
const CUSTOMER_INTERACTION_LOSS_TRIGGER_ID = "CUSTOMER_INTERACTION_LOSS_TRIGGER_ID";
const CUSTOMER_INTERACTION_DEPOSIT_TRIGGER_ID = "CUSTOMER_INTERACTION_DEPOSIT_TRIGGER_ID";

@Injectable({
	providedIn: "root",
})
export class MatNotificationsService {
	private readonly isDesktop: boolean;

	constructor(
		private store: Store<CoreContentState>,
		private snackbar: AbstractTranslatableSnackBarService,
		private imageSelector: ImageSelector,
		private alertService: AbstractTranslatableAlertService,
		private sessionSelector: SessionSelector,
		private deviceConfigService: DeviceConfigService,
		private matDialog: MatDialog,
		private sessionAction: SessionAction
	) {
		this.isDesktop = this.deviceConfigService.isDesktop();
	}

	openRgAlert(
		type: RgInteractionType,
		redirectButtonText: string,
		redirectButtonAction: Action | ActionWithPayload,
		notificationResultAction: NotificationResultRedirectAction
	) {
		return this.alertService.open$(
			{
				id: MAT_NOTIFICATIONS_UKGC_RG_INTERACTIONS_ALERT_ID,
				title: "rg-interactions.title",
				message: `rg-interactions.${type}.message`,
				hideClose: true,
				dialogConfig: { disableClose: true },
				mustBeInteracted: true,
				prefixJurisdiction: true,
				testId: `rg-interactions-${type}`,
				buttons: [
					{
						text: redirectButtonText,
						handler: () => {
							this.store.dispatch(
								batchActions(
									redirectButtonAction,
									NotificationResultAction.set({
										trigger: type,
										action: notificationResultAction,
									})
								)
							);
						},
						style: AlertButtonStyle.Transparent,
					},
					{
						text: "rg-interactions.button-continue",
						handler: () => {
							this.store.dispatch(
								NotificationResultAction.set({
									trigger: type,
									action: NotificationResultRedirectAction.Continue,
								})
							);
						},
					},
				],
			},
			[]
		);
	}

	openCustomerLimitSnackbar({
		message,
		type,
		doNotShowAgainButton,
		id = "",
		prefixJurisdiction = false,
		keepOpen = false,
		dismissibleDuration = 15000,
		duration,
		interpolateParams,
		dismissible = false,
	}: CustomerLimitSnackBarPayload) {
		return this.store.pipe(
			select(this.imageSelector.getByKey("general", "general.info-icon")),
			filter((icon) => !!icon),
			take(1),
			switchMap((icon) =>
				this.snackbar.openSnackBar$(
					{
						message,
						dismissibleDuration,
						dismissible,
						dismissibleDurationTranslationPrefix: "customer-limit-notification",
						keepOpen,
						icon,
						type,
						id,
						doNotShowAgainButton,
						prefixJurisdiction,
						duration,
						panelClass: ["large-width"],
					},
					[TranslationGroup.Common],
					interpolateParams
				)
			),
			map(() => undefined)
		);
	}

	openCustomerLimitAlert(
		limitPrefix: string,
		redirectButtonAction: Action | ActionWithPayload,
		prefixJurisdiction = false,
		buttonStyle?: AlertButtonStyle
	) {
		return this.alertService.open$(
			{
				id: LIMIT_ALERT_ID,
				title: `${limitPrefix}.title`,
				message: `${limitPrefix}.message`,
				hideClose: true,
				dialogConfig: { disableClose: true },
				mustBeInteracted: true,
				prefixJurisdiction,
				buttons: [
					{
						text: "customer-limit-notification.cancel",
						color: AlertButtonColor.Primary,
						style: buttonStyle,
					},
					{
						text: `${limitPrefix}.button`,
						handler: () => {
							this.store.dispatch(redirectButtonAction);
						},
						color: this.isDesktop ? AlertButtonColor.Accent : AlertButtonColor.Primary,
						style: buttonStyle,
					},
				],
			},
			[TranslationGroup.Common]
		);
	}

	openSessionLimitSnackbar(customerLimitSnackBarPayload: CustomerLimitSnackBarPayload) {
		return this.store.pipe(
			select(this.sessionSelector.getNextTimeLogin()),
			ignoreNil(),
			take(1),
			switchMap((nextTimeLogin) =>
				this.openCustomerLimitSnackbar({
					...customerLimitSnackBarPayload,
					interpolateParams: { nextTimeLogin },
				})
			)
		);
	}

	openSelfExclusionAlert$() {
		return this.alertService.open$(
			{
				id: SELF_EXCLUSION_ALERT_ID,
				mustBeInteracted: true,
				hideClose: true,
				messageComponent: LotbaSelfExclusionAlertContainer,
				messageComponentInit: (component: LotbaSelfExclusionAlertContainer) =>
					(component.data = {
						title: "self-exclusion.realtime-alert.title",
						redirectButton: "self-exclusion.realtime-alert.button",
						redirectInfo: "self-exclusion.realtime-alert.info",
					}),
				dialogConfig: {
					panelClass: "lotba-self-exclusion-wrapper",
					disableClose: true,
				},
			},
			[]
		);
	}

	openBalanceLimitExceededAlert$(payload: BalanceLimitExceededRealTimeMessagePayload) {
		return this.alertService.open$(
			{
				id: BALANCE_LIMIT_REACHED_ALERT_ID,
				mustBeInteracted: true,
				hideClose: true,
				prefixJurisdiction: true,
				messageComponent: KsaLimitsAlertContainer,
				messageComponentInit: (component: KsaLimitsAlertContainer) =>
					(component.data = {
						payload,
						ksaLimitAlertType: KsaLimitAlertType.BalanceLimit,
					}),
				dialogConfig: {
					panelClass: "ksa-limits-alert-wrapper",
					disableClose: true,
				},
				buttons: [
					{
						text: "limits-alert.button-logout",
						style: AlertButtonStyle.Transparent,
						color: AlertButtonColor.Primary,
						handler: () => {
							this.store.dispatch(LimitsReminderAction.fetchLimitsSummary(false));
							this.store.dispatch(CustomerInteractionsAction.markAlertAsLoggedOut(payload.MessageId));
							this.closeDialog(BALANCE_LIMIT_REACHED_ALERT_ID);
						},
					},
					{
						text: "limits-alert.button-continue-playing",
						style: AlertButtonStyle.Transparent,
						color: AlertButtonColor.Primary,
						handler: () => {
							this.store.dispatch(CustomerInteractionsAction.markAlertAsConfirmed(payload.MessageId));
							this.closeDialog(BALANCE_LIMIT_REACHED_ALERT_ID);
						},
					},
				],
			},
			[]
		);
	}

	openDepositLimitReachedAlert$(payload: DepositLimitReachedRealTimeMessagePayload) {
		return this.alertService.open$(
			{
				id: DEPOSIT_LIMIT_REACHED_ALERT_ID,
				mustBeInteracted: true,
				hideClose: true,
				prefixJurisdiction: true,
				messageComponent: KsaLimitsAlertContainer,
				messageComponentInit: (component: KsaLimitsAlertContainer) =>
					(component.data = {
						payload,
						ksaLimitAlertType: KsaLimitAlertType.DepositLimit,
					}),
				dialogConfig: {
					panelClass: "ksa-limits-alert-wrapper",
					disableClose: true,
				},
				buttons: [
					{
						text: "limits-alert.button-logout",
						handler: () => {
							this.store.dispatch(CustomerInteractionsAction.markAlertAsLoggedOut(payload.MessageId));
							this.closeDialog(DEPOSIT_LIMIT_REACHED_ALERT_ID);
						},
						style: AlertButtonStyle.Transparent,
						color: AlertButtonColor.Primary,
					},
					{
						text: "limits-alert.button-continue-playing",
						style: AlertButtonStyle.Transparent,
						color: AlertButtonColor.Primary,
						handler: () => {
							this.store.dispatch(CustomerInteractionsAction.markAlertAsConfirmed(payload.MessageId));
							this.closeDialog(DEPOSIT_LIMIT_REACHED_ALERT_ID);
						},
					},
				],
			},
			[]
		);
	}

	openBettingLimitReachedAlert$(payload: BettingLimitReachedRealTimeMessagePayload) {
		const isProductTypeCasino = payload.ProductCategories[0] === ProductType.Casino;
		const isProductTypeSportsbook = payload.ProductCategories[0] === ProductType.Sportsbook;

		if (isProductTypeCasino) {
			return this.openZhhAlert(payload, CORE_ROUTE.lobbies.casino);
		}

		if (isProductTypeSportsbook) {
			return this.openZhhAlert(payload, CORE_ROUTE.lobbies.sportsbook);
		}

		return this.openZhhAlert(payload, CORE_ROUTE.home);
	}

	openZhhAlert(payload: BettingLimitReachedRealTimeMessagePayload, exitGameRoute: string) {
		return this.alertService.open$(
			{
				id: BETTING_LIMIT_REACHED_ALERT_ID,
				mustBeInteracted: true,
				hideClose: true,
				prefixJurisdiction: true,
				messageComponent: ZhhBettingLimitsAlertContainer,
				messageComponentInit: (component: ZhhBettingLimitsAlertContainer) => (component.data = payload),
				dialogConfig: {
					disableClose: true,
				},
				buttons: [
					{
						color: AlertButtonColor.Accent,
						style: AlertButtonStyle.Flat,
						text: "responsible-gaming.return-to-lobby",
						handler: () => {
							this.store.dispatch(RouteAction.navigate(exitGameRoute));
						},
					},
					{
						color: AlertButtonColor.Primary,
						style: AlertButtonStyle.Transparent,
						text: "responsible-gaming.button-logout",
						handler: () => {
							this.store.dispatch(this.sessionAction.logout());
						},
					},
				],
			},
			[]
		);
	}

	openSessionLimitReachedAlert$() {
		return this.alertService.open$(
			{
				id: SESSION_LIMIT_REACHED_ALERT_ID,
				mustBeInteracted: true,
				hideClose: true,
				prefixJurisdiction: true,
				messageComponent: KsaLimitsAlertContainer,
				messageComponentInit: (component: KsaLimitsAlertContainer) =>
					(component.data = {
						ksaLimitAlertType: KsaLimitAlertType.SessionLimit,
					}),
				dialogConfig: {
					panelClass: "ksa-limits-alert-wrapper",
					disableClose: true,
				},
				buttons: [
					{
						text: "general.close",
						style: AlertButtonStyle.Transparent,
						color: AlertButtonColor.Primary,
					},
				],
			},
			[]
		);
	}

	openCustomerInteractionsTimeTriggerAlert$(messageId: string) {
		return this.openCustomerInteractionAlert(
			messageId,
			"customer-interactions.time-trigger-alert-title",
			notificationMessageKeys.timeTriggerAlertDescription,
			"customer-interactions.continue",
			"general.logout",
			() => {
				this.store.dispatch(CustomerInteractionsAction.markAlertAsLoggedOut(messageId));
				this.closeDialog(CUSTOMER_INTERACTION_TIME_TRIGGER_ID);
			}
		);
	}

	openCustomerInteractionsToolsTriggerAlert$(messageId: string) {
		return this.openCustomerInteractionAlert(
			messageId,
			"customer-interactions.tools-trigger-alert-title",
			notificationMessageKeys.toolsTriggerAlertDescription,
			"customer-interactions.continue",
			"customer-interactions.more-information",
			() => {
				this.store.dispatch(
					batchActions(
						CustomerInteractionsAction.markAlertAsMoreInfo(messageId),
						RouteAction.navigate(CORE_ROUTE.responsibleGaming.selfExclusion)
					)
				);
				this.closeDialog(CUSTOMER_INTERACTION_TOOLS_TRIGGER_ID);
			}
		);
	}

	openCustomerInteractionsWageringTriggerAlert$(messageId: string) {
		return this.openCustomerInteractionAlert(
			messageId,
			"customer-interactions.wagering-trigger-alert-title",
			notificationMessageKeys.wageringTriggerAlertDescription,
			"customer-interactions.continue",
			"customer-interactions.more-information",
			() => {
				this.store.dispatch(
					batchActions(
						CustomerInteractionsAction.markAlertAsMoreInfo(messageId),
						RouteAction.navigate(CORE_ROUTE.responsibleGaming.timeOff)
					)
				);
				this.closeDialog(CUSTOMER_INTERACTION_WAGERING_TRIGGER_ID);
			}
		);
	}

	openCustomerInteractionsLossTriggerAlert$(messageId: string) {
		return this.openCustomerInteractionAlert(
			messageId,
			"customer-interactions.loss-trigger-alert-title",
			notificationMessageKeys.lossTriggerAlertDescription,
			"customer-interactions.continue",
			"customer-interactions.more-information",
			() => {
				this.store.dispatch(
					batchActions(
						CustomerInteractionsAction.markAlertAsMoreInfo(messageId),
						RouteAction.navigate(CORE_ROUTE.responsibleGaming.netlossLimit)
					)
				);
				this.closeDialog(CUSTOMER_INTERACTION_LOSS_TRIGGER_ID);
			}
		);
	}

	openCustomerInteractionsDepositTriggerAlert$(messageId: string) {
		return this.openCustomerInteractionAlert(
			messageId,
			"customer-interactions.deposit-trigger-alert-title",
			notificationMessageKeys.depositTriggerAlertDescription,
			"customer-interactions.continue",
			"customer-interactions.deposit-limit",
			() => {
				this.store.dispatch(
					batchActions(
						CustomerInteractionsAction.markAlertAsDepositLimit(messageId),
						RouteAction.navigate(CORE_ROUTE.responsibleGaming.depositLimit)
					)
				);
				this.closeDialog(CUSTOMER_INTERACTION_DEPOSIT_TRIGGER_ID);
			}
		);
	}

	private openCustomerInteractionAlert(
		messageId: string,
		title: string,
		message: string,
		primaryButtonText: string,
		secondaryButtonText: string,
		secondaryButtonHandler: () => void
	) {
		return this.alertService.open$(
			{
				id: CUSTOMER_INTERACTION_TIME_TRIGGER_ID,
				mustBeInteracted: true,
				hideClose: true,
				title,
				message,
				dialogConfig: {
					disableClose: true,
				},
				buttons: [
					{
						text: secondaryButtonText,
						handler: secondaryButtonHandler,
						color: AlertButtonColor.Primary,
						style: AlertButtonStyle.Stroked,
					},
					{
						text: primaryButtonText,
						handler: () => {
							this.store.dispatch(CustomerInteractionsAction.markAlertAsConfirmed(messageId));
							this.closeDialog(CUSTOMER_INTERACTION_TIME_TRIGGER_ID);
						},
					},
				],
			},
			[]
		);
	}

	private closeDialog(dialogId: string) {
		const openDialog = this.matDialog.openDialogs.find((dialog) => dialog.id === dialogId);
		if (openDialog) {
			openDialog.close();
		}
	}
}
